"use client";

import { useEffect, useRef, useState } from "react";
import { Download, X } from "lucide-react";
import type { PDFDocumentProxy } from "pdfjs-dist/legacy/build/pdf.mjs";
import type { PdfEditorElement } from "./types";

type PdfPreviewModalProps = {
  fileUrl: string;
  elements: PdfEditorElement[];
  priceLabel: string;
  role?: string;
  watermarked?: boolean;
  onClose: () => void;
  onCheckout: () => void;
};

type PageInfo = {
  widthPt: number;
  heightPt: number;
  canvasWidth: number;
  canvasHeight: number;
  scale: number;
};

let pdfWorkerConfigured = false;

type PdfJsModule = typeof import("pdfjs-dist/legacy/build/pdf.mjs");

const importPdfJs = new Function("specifier", "return import(specifier)") as (
  specifier: string,
) => Promise<PdfJsModule>;

async function configurePdfWorker() {
  if (pdfWorkerConfigured || typeof window === "undefined") {
    return;
  }

  const pdfjs = await importPdfJs("/pdf.mjs");
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
  pdfWorkerConfigured = true;
}

function toCanvasRect(element: PdfEditorElement, pageInfo: PageInfo) {
  return {
    x: element.x * pageInfo.scale,
    y: (pageInfo.heightPt - element.y - element.height) * pageInfo.scale,
    width: element.width * pageInfo.scale,
    height: element.height * pageInfo.scale,
  };
}

export function PdfPreviewModal({
  fileUrl,
  elements,
  priceLabel,
  role,
  watermarked = false,
  onClose,
  onCheckout,
}: PdfPreviewModalProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [status, setStatus] = useState("Ladowanie podgladu...");

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setStatus("Ladowanie podgladu...");
      await configurePdfWorker();
      const pdfjs = await importPdfJs("/pdf.mjs");
      const document = await pdfjs.getDocument(fileUrl).promise;

      if (!cancelled) {
        setPdfDocument(document);
        setStatus("");
      }
    }

    loadPdf().catch(() => {
      if (!cancelled) {
        setStatus("Nie udalo sie wyrenderowac podgladu PDF.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  useEffect(() => {
    let cancelled = false;

    async function renderPage() {
      if (!pdfDocument) {
        return;
      }

      const page = await pdfDocument.getPage(currentPage);
      const baseViewport = page.getViewport({ scale: 1 });
      const availableWidth = Math.max(320, (viewportRef.current?.clientWidth || 900) - 32);
      const isPhoneLandscape = window.matchMedia("(max-width: 980px) and (max-height: 520px) and (orientation: landscape)").matches;
      const availableHeight = Math.max(240, (viewportRef.current?.clientHeight || 700) - 20);
      const fitScale = Math.min(
        2,
        availableWidth / baseViewport.width,
        isPhoneLandscape ? availableHeight / baseViewport.height : 2,
      );
      const renderScale = fitScale * zoom;
      const viewport = page.getViewport({ scale: renderScale });
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");

      if (!canvas || !context || cancelled) {
        return;
      }

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = `${viewport.width}px`;
      canvas.style.height = `${viewport.height}px`;
      context.clearRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvas, canvasContext: context, viewport }).promise;

      if (!cancelled) {
        setPageInfo({
          widthPt: baseViewport.width,
          heightPt: baseViewport.height,
          canvasWidth: viewport.width,
          canvasHeight: viewport.height,
          scale: renderScale,
        });
      }
    }

    renderPage().catch(() => {
      if (!cancelled) {
        setStatus("Nie udalo sie wyrenderowac strony PDF.");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentPage, pdfDocument, zoom]);

  const pageCount = pdfDocument?.numPages || 1;
  const visibleElements = pageInfo ? elements.filter((element) => element.page === currentPage) : [];

  return (
    <div className="preview-modal" role="dialog" aria-modal="true" aria-label="Podglad PDF">
      <header className="preview-bar">
        <div className="pdf-editor-page-actions">
          <button className="secondary" type="button" disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => page - 1)}>
            <span className="preview-button-full">Poprzednia</span>
            <span className="preview-button-short">Poprz.</span>
          </button>
          <span>
            Strona {currentPage} / {pageCount}
          </span>
          <button className="secondary" type="button" disabled={currentPage >= pageCount} onClick={() => setCurrentPage((page) => page + 1)}>
            <span className="preview-button-full">Nastepna</span>
            <span className="preview-button-short">Nast.</span>
          </button>
        </div>
        <label className="pdf-editor-zoom">
          <span className="zoom-label">Zoom</span>
          <input type="range" min="0.7" max="2" step="0.1" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} />
          <b>{Math.round(zoom * 100)}%</b>
        </label>
        <div className="toolbar-actions">
          <span className="price-chip">{priceLabel}</span>
          <button className="primary" type="button" onClick={onCheckout}>
            <Download size={18} />
            {role === "admin" || priceLabel === "0 PLN" ? "Pobierz (Darmowe dla Admina)" : "Zapłać i pobierz"}
          </button>
          <button className="secondary icon-button" type="button" aria-label="Zamknij podgląd" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
      </header>

      <div ref={viewportRef} className="preview-canvas-wrap">
        {status ? <p className="pdf-editor-status">{status}</p> : null}
        <div
          className="preview-page"
          style={{
            width: pageInfo?.canvasWidth || 0,
            height: pageInfo?.canvasHeight || 0,
          }}
        >
          <canvas ref={canvasRef} className="pdf-editor-canvas" />
          {pageInfo
            ? visibleElements.map((element) => {
                const rect = toCanvasRect(element, pageInfo);
                return (
                  <div
                    className={`preview-element preview-element-${element.type}`}
                    key={element.id}
                    style={{
                      left: rect.x,
                      top: rect.y,
                      width: rect.width,
                      height: rect.height,
                      fontSize: (element.style?.fontSize || 15) * (rect.height / Math.max(element.height, 1)),
                    }}
                  >
                    {element.type === "mask" ? null : element.type === "signature" && element.assetPath ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={element.assetPath} alt="" />
                    ) : (
                      element.value
                    )}
                  </div>
                );
              })
            : null}
          {watermarked ? (
            <div className="preview-demo-watermark" aria-hidden="true">
              {Array.from({ length: 24 }, (_, index) => (
                <span key={index}>wypelnijpdf.pl · podgląd demo</span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
